/**
 * OnboardingChecklistModal — the "you need to finish setup" popup.
 *
 * Sachin 2026-03-05: "A parent said they cannot put a card in... Should
 * have a pop-up that shows up when they need to do the waiver and the
 * credit card."
 *
 * Sachin 2026-06: "Some parents say they can't find the waiver. If it's
 * not filled out, get them to easily find it — maybe both a pop up and a
 * button at the bottom." The popup now:
 *   - lists a row PER missing waiver (the account holder AND each athlete
 *     who still needs one), not just a single self row;
 *   - keeps reappearing every visit while ANY waiver is unsigned (the
 *     permanent "don't show again" opt-out only applies when the sole
 *     outstanding item is the card);
 *   - never re-shows once every waiver is signed.
 *
 * Card action opens Stripe Checkout directly; every waiver row jumps to
 * /welcome/waiver, which hosts the per-athlete batch-signing UI.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    FileSignature, CreditCard, ShieldCheck, CheckCircle2, Loader2, ArrowRight,
    User as UserIcon, Baby,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingChecklistModal({ missingWaivers = [], needsCard, onDismiss }) {
    const navigate = useNavigate();
    const [busyCard, setBusyCard] = useState(false);

    const needsWaiver = missingWaivers.length > 0;
    // Nothing to do → parent shouldn't see the modal at all.
    if (!needsWaiver && !needsCard) return null;

    const openWaiver = () => navigate("/welcome/waiver");

    const openStripe = async () => {
        setBusyCard(true);
        try {
            const r = await axios.post(
                `${API}/billing/setup-card`,
                { origin_url: window.location.origin },
                { withCredentials: true },
            );
            if (!r.data?.url) throw new Error("Stripe didn't return a checkout URL");
            window.location.href = r.data.url;
        } catch (e) {
            toast.error("Couldn't open Stripe Checkout", {
                description: e?.response?.data?.detail || e?.message,
            });
            setBusyCard(false);
        }
    };

    return (
        <div
            data-testid="onboarding-checklist-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-6"
        >
            <div className="relative w-full max-w-lg bg-brand-obsidian border border-zinc-800 rounded-xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="absolute inset-x-0 top-0 h-1 bg-brand-red" aria-hidden="true" />
                <div className="p-6 sm:p-8">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                        <ShieldCheck className="w-3 h-3" />
                        Finish setup
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl tracking-wider text-white leading-tight">
                        {needsWaiver
                            ? "Please sign your waiver to get on the floor."
                            : "One quick thing before you're on the floor."}
                    </h2>
                    <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                        {needsWaiver
                            ? "Every athlete needs a signed participant agreement before their first session. Tap below to sign — it takes about 30 seconds each."
                            : "Add a card on file so your coach can charge sessions in one tap — no chasing payment."}
                    </p>

                    <div className="mt-6 space-y-3">
                        {/* One row per missing waiver — account holder + each athlete. */}
                        {missingWaivers.map((w) => (
                            <ChecklistRow
                                key={w.key}
                                testId={`onboarding-row-waiver-${w.key}`}
                                icon={w.kind === "self" ? UserIcon : Baby}
                                title={w.kind === "self" ? "Sign your waiver" : `Sign ${w.label}'s waiver`}
                                subtitle="Participant agreement · ~30 seconds"
                                done={false}
                                ctaLabel="Sign now"
                                onClick={openWaiver}
                            />
                        ))}
                        {/* Card row — shows a green "Done" state if a card is
                            already on file, otherwise the Add-card action. */}
                        <ChecklistRow
                            testId="onboarding-row-card"
                            icon={CreditCard}
                            title="Add a card on file"
                            subtitle="Stripe-vaulted · $0 charged today · cancel anytime"
                            done={!needsCard}
                            ctaLabel="Add card"
                            busy={busyCard}
                            onClick={openStripe}
                        />
                    </div>

                    <p className="text-[11px] text-zinc-500 mt-6 leading-relaxed">
                        Card data never touches our servers — Stripe-hosted Checkout (PCI SAQ-A). Every charge is sent to you for approval first.
                    </p>

                    {/* Dismiss controls. While ANY waiver is unsigned the parent
                        can close the popup for THIS visit but it returns next
                        time (Sachin 2026-06). The permanent "don't show again"
                        opt-out is only offered when the sole outstanding item
                        is the card. */}
                    <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-between gap-3 items-stretch sm:items-center">
                        {!needsWaiver ? (
                            <button
                                type="button"
                                data-testid="onboarding-dismiss-forever"
                                onClick={() => {
                                    try {
                                        window.localStorage.setItem(
                                            "maxsc_onboarding_dismissed_forever",
                                            new Date().toISOString(),
                                        );
                                    } catch (_e) { /* private mode → noop */ }
                                    onDismiss();
                                }}
                                className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-zinc-200 underline underline-offset-2"
                            >
                                Don&apos;t show this again
                            </button>
                        ) : (
                            <span className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80">
                                Required before your next session
                            </span>
                        )}
                        <button
                            type="button"
                            data-testid="onboarding-close"
                            onClick={onDismiss}
                            className="text-[11px] tracking-[0.2em] uppercase text-zinc-200 hover:text-white border border-zinc-700 hover:border-zinc-500 px-4 py-2 rounded transition-colors"
                        >
                            {needsWaiver ? "Later" : "Close"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChecklistRow({ testId, icon: Icon, title, subtitle, done, ctaLabel, busy, onClick }) {
    return (
        <div
            data-testid={testId}
            className={`border rounded-lg p-4 flex items-center gap-4 ${
                done ? "border-emerald-700/40 bg-emerald-900/10" : "border-zinc-800 bg-zinc-950/60"
            }`}
        >
            <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                    done
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : "border-brand-red/40 bg-brand-red/10 text-brand-red"
                }`}
            >
                {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-semibold">{title}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{subtitle}</div>
            </div>
            {done ? (
                <span
                    data-testid={`${testId}-done`}
                    className="text-[10px] tracking-[0.2em] uppercase text-emerald-300 whitespace-nowrap"
                >
                    Done
                </span>
            ) : (
                <button
                    type="button"
                    data-testid={`${testId}-cta`}
                    onClick={onClick}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 transition-colors px-3 py-2 rounded disabled:opacity-60 whitespace-nowrap"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {ctaLabel}
                    <ArrowRight className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}
